import { Suspense, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useMachine } from "@xstate/react";
import type { Feature, MultiPolygon, Polygon } from "geojson";
import { useAtom } from "jotai";
import { ErrorBoundary } from "react-error-boundary";
import { Pane as LeafletPane, MapContainer, ScaleControl } from "react-leaflet";
import { activeOverlayersAtom, runYearAtom } from "src/atoms/overlayers";
import { LayerManagerOnMap } from "src/components/LayerManager/LayerManagerOnMap";
import { PaneLoader } from "src/components/loaders/PaneLoader";
import { CoordinatesControl } from "src/components/map/CoordinatesControl";
import { MapControlBase } from "src/components/map/MapControlBase";
import { MapSizeObserver } from "src/components/map/MapSizeObserver";
import { MapZoomControls } from "src/components/map/MapZoomControls";
import { SearchControl } from "src/components/map/SearchControl";
import { OverlayersLegend } from "src/components/OverlayersLegend";
import { ActiveOverlayersOnMap } from "src/components/on-map/ActiveOverlayersOnMap";
import { BaselayerOnMap } from "src/components/on-map/BaselayerOnMap";
import { Pane } from "src/components/Pane";
import {
  CaseCreateForm,
  type CaseCreateFormValues,
} from "src/forms/CaseCreateForm";
import { CaseCreatePredefinedForm } from "src/forms/CaseCreatePredefinedForm";
import {
  useBoundsWithoutCase,
  useCaseCreatePredefinedMutation,
} from "src/hooks";
import {
  getDefaultCutoffLowerLimit,
  useRunYears,
} from "src/hooks/runYearHooks";
import { CaseLayout } from "src/layouts/CaseLayout";
import { caseCreateMachine } from "src/machines/case-create.machine";
import { CasePickShape } from "src/pages/Case/CasePickShape";
import { CasePickShapeCreation } from "src/pages/Case/CasePickShapeCreation";
import { CaseSelectFeatureLayer } from "src/pages/Case/CaseSelectFeatureLayer";
import { CaseUploadShape } from "src/pages/Case/CaseUploadShape";
import { PredefinedLEZLayerInitializer } from "src/pages/Case/PredefinedLEZLayerInitializer";
import { SelectedCaseFeaturePreview } from "src/pages/Case/SelectedCaseFeaturePreview";
import type { FeatureSelectionLayer } from "src/types";

export function CaseCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bounds = useBoundsWithoutCase();
  const [activeLayers, setActiveLayers] = useAtom(activeOverlayersAtom);
  const [, setRunYear] = useAtom(runYearAtom);
  const { data: runYears } = useRunYears();

  const [activeLayersSnapshot, setActiveLayersSnapshot] = useState<
    typeof activeLayers
  >([]);

  function deactivateAllLayers() {
    setActiveLayers(activeLayers.map((l) => ({ ...l, isActive: false })));
  }

  const [selectedFeatureSelectionLayer, setSelectedFeatureSelectionLayer] =
    useState<FeatureSelectionLayer | null>(null);
  const mapRef = useRef<L.Map>(null);

  const createPredefinedMutation = useCaseCreatePredefinedMutation();

  const [state, send] = useMachine(caseCreateMachine, {
    actions: {
      exit: () => navigate("/cases"),

      submit: (context) => {
        createPredefinedMutation
          .mutateAsync({
            name: context.name,
            description: context.description,
            userSrid: Number.parseInt(context.userSrid, 10),
            polygonList_frontendSrid: context.polygonList_frontendSrid,
            runYear: context.runYear || new Date().getFullYear(),
            cutoff: context.cutoff || undefined,
          })
          .then(() => send({ type: "CREATION_SUCCESS" }));
      },
      submitNormal: (...args) =>
        console.log("machine action > submitNormal", args),
    },
    // devTools: true,
  });
  return render();

  function render() {
    return (
      <CaseLayout>
        <Styles>
          {renderCaseCreateState()}
          <MapContainer
            ref={mapRef}
            bounds={bounds}
            zoomControl={false}
            className="map-container"
          >
            <LeafletPane name="drawing-pane" style={{ zIndex: 9998 }} />
            <LeafletPane name="drawing-pane-vertex" style={{ zIndex: 9999 }} />
            {state.matches("pickFeature") && state.context.runYear && (
              <CaseSelectFeatureLayer
                selectedLayer={selectedFeatureSelectionLayer}
                runYear={state.context.runYear}
              />
            )}
            {state.matches("pickFeature") &&
              state.context.polygons.length > 0 && (
                <SelectedCaseFeaturePreview features={state.context.polygons} />
              )}
            <SearchControl position="topleft" />
            <ScaleControl position="bottomleft" imperial={false} />
            <MapZoomControls position="bottomright" />
            <MapSizeObserver />
            <LayerManagerOnMap defaultOpen={true} context="case-create" />
            <MapControlBase position="bottomleft">
              <CoordinatesControl />
            </MapControlBase>
            <MapControlBase position="bottomleft">
              <ErrorBoundary fallback={null}>
                <Suspense fallback={null}>
                  <OverlayersLegend context="case-create" />
                </Suspense>
              </ErrorBoundary>
            </MapControlBase>
            <BaselayerOnMap />
            {state.context.runYear && (
              <ErrorBoundary fallback={null}>
                <Suspense fallback={null}>
                  <PredefinedLEZLayerInitializer />
                </Suspense>
              </ErrorBoundary>
            )}
            <ErrorBoundary fallback={null}>
              <Suspense fallback={null}>
                <ActiveOverlayersOnMap context="case-create" />
              </Suspense>
            </ErrorBoundary>
          </MapContainer>
        </Styles>
      </CaseLayout>
    );
  }
  function renderCaseCreateState() {
    if (state.matches("new")) {
      return (
        <Suspense
          fallback={
            <Typography variant="body1">
              {<PaneLoader message={t("label.loading")} />}
            </Typography>
          }
        >
          <Pane>
            <CaseCreateForm
              onCancel={() => send({ type: "PREVIOUS" })}
              onSubmit={(values: CaseCreateFormValues) => {
                const selectedRunYear = runYears?.find(
                  (runYear) => runYear.year === values.runYear
                );

                setRunYear(
                  selectedRunYear
                    ? {
                        ...selectedRunYear,
                        cutoff:
                          values.cutoff ??
                          getDefaultCutoffLowerLimit(selectedRunYear.cutoffList),
                      }
                    : undefined
                );

                send({
                  type: "PICK_GEOMETRY_CREATION",
                  payload: { ...state.context, ...values },
                });
              }}
              values={state.context}
            />
          </Pane>
        </Suspense>
      );
    }
    if (["pickGeometryCreation"].some(state.matches) && state.context.runYear) {
      return (
        <CasePickShapeCreation
          runYear={state.context.runYear}
          onPrevious={() => {
            setRunYear(undefined);
            send({ type: "CLEAR_POLYGONS" });
            send({ type: "PREVIOUS" });
          }}
          onNext={(option) => {
            if (option === "upload") {
              send({ type: "UPLOAD_FEATURE", payload: { ...state.context } });
            } else if (option === "draw") {
              send({ type: "DRAW_FEATURE", payload: { ...state.context } });
            } else if (option === "pick") {
              setActiveLayersSnapshot(activeLayers);
              deactivateAllLayers();
              send({ type: "PICK_FEATURE", payload: { ...state.context } });
            }
          }}
        />
      );
    }
    if (["pickFeature"].some(state.matches) && state.context.runYear) {
      return (
        <CasePickShape
          selectedLayer={selectedFeatureSelectionLayer}
          runYear={state.context.runYear}
          onPrevious={() => {
            setActiveLayers(activeLayersSnapshot);
            send({ type: "CLEAR_POLYGONS" });
            send({ type: "PREVIOUS" });
          }}
          onNext={() => {
            setActiveLayers(activeLayersSnapshot);
            send({ type: "NEXT", payload: { ...state.context } });
          }}
          setSelectedLayer={(l) => {
            send({ type: "CLEAR_POLYGONS" });
            setSelectedFeatureSelectionLayer(l);
          }}
          features={state.context.polygons}
          onShapesSelected={(features) => {
            send({ type: "STORE_POLYGONS", payload: { polygons: features } });
          }}
          map={mapRef.current}
        />
      );
    }

    if (["uploadFeature"].some(state.matches)) {
      return (
        <CaseUploadShape
          onNext={(
            polygon: Feature<Polygon | MultiPolygon>,
            warningList: string[]
          ) => {
            send({
              type: "NEXT",
              payload: { ...state.context, polygons: [polygon], warningList },
            });
          }}
          onPrevious={() => {
            send({ type: "CLEAR_POLYGONS" });
            send({ type: "PREVIOUS" });
          }}
        />
      );
    }
    if (["drawFeature", "submitting"].some(state.matches)) {
      return (
        <Pane>
          {mapRef.current && (
            <CaseCreatePredefinedForm
              clearPolygons={() => send({ type: "CLEAR_POLYGONS" })}
              clearWarningList={() => send({ type: "CLEAR_WARNING_LIST" })}
              initialPolygons={state.context.polygons}
              warningList={state.context.warningList}
              map={mapRef.current}
              onCancel={() => {
                send({ type: "CLEAR_POLYGONS" });
                send({ type: "PREVIOUS" });
              }}
              onSubmit={(polygonList_frontendSrid: string[]) => {
                send({
                  type: "NEXT",
                  payload: { ...state.context, polygonList_frontendSrid },
                });
              }}
              isLoading={createPredefinedMutation.isLoading}
              errors={createPredefinedMutation.error?.response?.data}
            />
          )}
        </Pane>
      );
    }
    return null;
  }
}

const Styles = styled("div")`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: auto;

  .map-container,
  .leaflet-container {
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }
`;
